import React, { useState, useEffect } from 'react';
import axios from 'axios';

const CooperDashboard = () => {
  const [loggedIn, setLoggedIn] = useState(false);
  const [requests, setRequests] = useState([]);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [filter, setFilter] = useState('ALL');
  const [rejectReasons, setRejectReasons] = useState({});
  const [isMobile, setIsMobile] = useState(false);

  // **Handle responsive design**
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // **Login keydown handler**
  const handleLoginKeyDown = (e) => {
    if (e.key === 'Enter') login();
  };

  // **Login function**
  const login = () => {
    if (username === 'Cooper' && password === 'Steel') {
      setLoggedIn(true);
      fetchRequests();
    } else {
      alert('Invalid credentials');
    }
  };

  // **Fetch requests from API**
  const fetchRequests = async () => {
    try {
      const resp = await axios.get('/api/cooper-requests');
      let data = resp.data;
      data.sort((a, b) => {
        if (!a.dateNeeded) return 1;
        if (!b.dateNeeded) return -1;
        return new Date(a.dateNeeded) - new Date(b.dateNeeded);
      });
      setRequests(data);
    } catch (error) {
      console.error('Error fetching requests:', error);
    }
  };

  // **Approve request**
  const approveRequest = async (id) => {
    try {
      await axios.post(`/api/cooper-requests/${id}/approve`);
      fetchRequests();
    } catch (error) {
      console.error('Error approving request:', error);
    }
  };

  // **Fulfill request**
  const fulfillRequest = async (id) => {
    try {
      await axios.post(`/api/cooper-requests/${id}/fulfill`);
      fetchRequests();
    } catch (error) {
      console.error('Error fulfilling request:', error);
    }
  };

  // **Handle reject click**
  const handleRejectClick = (id) => {
    setRejectReasons((prev) => ({ ...prev, [id]: '' }));
  };

  // **Handle reject reason change**
  const handleRejectChange = (id, value) => {
    setRejectReasons((prev) => ({ ...prev, [id]: value }));
  };

  // **Submit rejection**
  const submitRejection = async (id) => {
    const reason = rejectReasons[id] || '';
    try {
      await axios.post(`/api/cooper-requests/${id}/reject`, { reason });
      fetchRequests();
      setRejectReasons((prev) => {
        const newObj = { ...prev };
        delete newObj[id];
        return newObj;
      });
    } catch (error) {
      console.error('Error rejecting request:', error);
    }
  };

  // **Filter requests**
  const filteredRequests = requests.filter((req) => {
    if (filter === 'ALL') return true;
    return req.status && req.status.toUpperCase() === filter;
  });

  // **Render mobile cards**
  const renderCards = () => (
    <div className="flex flex-col gap-4 p-2.5">
      {filteredRequests.map((req) => (
        <div
          key={req._id}
          className={`bg-gray-800 p-5 rounded-xl text-white text-[15px] shadow-lg border border-gray-700 ${
            req.status === 'Opened'
              ? 'bg-yellow-500/15'
              : req.status === 'Approved'
              ? 'bg-green-500/15'
              : req.status === 'Rejected'
              ? 'bg-red-500/15'
              : 'bg-cyan-500/15'
          }`}
        >
          <div className="flex flex-wrap mb-3 gap-2">
            <strong className="text-gray-400">Requester:</strong>
            <span className="flex-1 break-words">{req.firstName} {req.lastName}</span>
          </div>
          <div className="flex flex-wrap mb-3 gap-2">
            <strong className="text-gray-400">Equipment:</strong>
            <span className="flex-1 break-words">{req.equipmentNeeds.join(', ')}</span>
          </div>
          <div className="flex flex-wrap mb-3 gap-2">
            <strong className="text-gray-400">Date Needed:</strong>
            <span className="flex-1 break-words">
              {req.dateNeeded ? req.dateNeeded.slice(0, 10) : 'N/A'}
            </span>
          </div>
          <div className="flex flex-wrap mb-3 gap-2">
            <strong className="text-gray-400">Reason:</strong>
            <span className="flex-1 break-words">{req.reason}</span>
          </div>
          <div className="flex flex-wrap mb-3 gap-2">
            <strong className="text-gray-400">Status:</strong>
            <span className="flex-1 break-words">{req.status}</span>
          </div>
          <div className="flex flex-wrap justify-center gap-3 mt-4 pt-3 border-t border-gray-700">
            {req.status === 'Opened' && (
              <>
                <button
                  className="py-2 px-4 rounded-full font-semibold m-1 border-none cursor-pointer text-sm bg-green-500 text-white hover:scale-105 hover:opacity-90"
                  onClick={() => approveRequest(req._id)}
                >
                  Approve
                </button>
                {rejectReasons[req._id] === undefined ? (
                  <button
                    className="py-2 px-4 rounded-full font-semibold m-1 border-none cursor-pointer text-sm bg-red-500 text-white hover:scale-105 hover:opacity-90"
                    onClick={() => handleRejectClick(req._id)}
                  >
                    Reject
                  </button>
                ) : (
                  <div className="flex gap-2 mt-2 w-full">
                    <input
                      type="text"
                      placeholder="Reason for rejection"
                      value={rejectReasons[req._id]}
                      onChange={(e) => handleRejectChange(req._id, e.target.value)}
                      className="p-1.5 border border-gray-600 rounded text-sm bg-gray-800 text-white flex-1"
                    />
                    <button
                      className="py-2 px-4 rounded-full font-semibold m-1 border-none cursor-pointer text-sm bg-red-500 text-white hover:scale-105 hover:opacity-90"
                      onClick={() => submitRejection(req._id)}
                    >
                      Submit
                    </button>
                  </div>
                )}
              </>
            )}
            {req.status === 'Approved' && (
              <button
                className="py-2 px-4 rounded-full font-semibold m-1 border-none cursor-pointer text-sm bg-cyan-500 text-white hover:scale-105 hover:opacity-90"
                onClick={() => fulfillRequest(req._id)}
              >
                Fulfill
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  // **Render desktop table**
  const renderTable = () => (
    <table className="w-full border-collapse mt-2">
      <thead>
        <tr>
          <th className="border border-gray-700 p-2.5 text-left text-sm bg-gray-800">Requester</th>
          <th className="border border-gray-700 p-2.5 text-left text-sm bg-gray-800">Equipment Needs</th>
          <th className="border border-gray-700 p-2.5 text-left text-sm bg-gray-800">Date Needed</th>
          <th className="border border-gray-700 p-2.5 text-left text-sm bg-gray-800">Reason</th>
          <th className="border border-gray-700 p-2.5 text-left text-sm bg-gray-800">Status</th>
          <th className="border border-gray-700 p-2.5 text-left text-sm bg-gray-800">Action</th>
        </tr>
      </thead>
      <tbody>
        {filteredRequests.map((req) => (
          <tr
            key={req._id}
            className={`border border-gray-700 ${
              req.status === 'Opened'
                ? 'bg-yellow-500/10'
                : req.status === 'Approved'
                ? 'bg-green-500/10'
                : req.status === 'Rejected'
                ? 'bg-red-500/10'
                : 'bg-cyan-500/10'
            }`}
          >
            <td className="p-2.5 text-left text-sm">{req.firstName} {req.lastName}</td>
            <td className="p-2.5 text-left text-sm">{req.equipmentNeeds.join(', ')}</td>
            <td className="p-2.5 text-left text-sm">
              {req.dateNeeded ? req.dateNeeded.slice(0, 10) : ''}
            </td>
            <td className="p-2.5 text-left text-sm">{req.reason}</td>
            <td className="p-2.5 text-left text-sm">{req.status}</td>
            <td className="p-2.5 text-left text-sm">
              {req.status === 'Opened' && (
                <>
                  <button
                    className="py-2 px-4 rounded-full font-semibold m-1 border-none cursor-pointer text-sm bg-green-500 text-white hover:scale-105 hover:opacity-90"
                    onClick={() => approveRequest(req._id)}
                  >
                    Approve
                  </button>
                  {rejectReasons[req._id] === undefined ? (
                    <button
                      className="py-2 px-4 rounded-full font-semibold m-1 border-none cursor-pointer text-sm bg-red-500 text-white hover:scale-105 hover:opacity-90"
                      onClick={() => handleRejectClick(req._id)}
                    >
                      Reject
                    </button>
                  ) : (
                    <div className="flex gap-2 mt-2">
                      <input
                        type="text"
                        placeholder="Reason for rejection"
                        value={rejectReasons[req._id]}
                        onChange={(e) => handleRejectChange(req._id, e.target.value)}
                        className="p-1.5 border border-gray-600 rounded text-sm bg-gray-800 text-white flex-1"
                      />
                      <button
                        className="py-2 px-4 rounded-full font-semibold m-1 border-none cursor-pointer text-sm bg-red-500 text-white hover:scale-105 hover:opacity-90"
                        onClick={() => submitRejection(req._id)}
                      >
                        Submit
                      </button>
                    </div>
                  )}
                </>
              )}
              {req.status === 'Approved' && (
                <button
                  className="py-2 px-4 rounded-full font-semibold m-1 border-none cursor-pointer text-sm bg-cyan-500 text-white hover:scale-105 hover:opacity-90"
                  onClick={() => fulfillRequest(req._id)}
                >
                  Fulfill
                </button>
              )}
              {['Rejected', 'Fulfilled'].includes(req.status) && <span>—</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  // **Login screen**
  if (!loggedIn) {
    return (
      <div
        className="h-screen w-full bg-black flex flex-col items-center justify-center text-white text-center p-5"
        onKeyDown={handleLoginKeyDown}
      >
        <div className="mb-5">
          <img src="/LogoFile.png" alt="Myte Logo" className="max-w-[180px] mb-4" />
          <h1 className="text-4xl mb-2 text-white">Cooper Dashboard</h1>
          <p className="text-lg my-1 text-gray-300">Manage Equipment Requests with Ease</p>
          <p className="text-base text-gray-400">
            Approve, Reject, Fulfill, and Review Requests Quickly
          </p>
        </div>
        <div className="flex flex-col items-center">
          <input
            className="p-3 mb-2 w-[280px] max-w-[90%] border border-gray-700 rounded bg-gray-800 text-white text-base placeholder-gray-500"
            placeholder="Username"
            onChange={(e) => setUsername(e.target.value)}
          />
          <input
            className="p-3 mb-2 w-[280px] max-w-[90%] border border-gray-700 rounded bg-gray-800 text-white text-base placeholder-gray-500"
            type="password"
            placeholder="Password"
            onChange={(e) => setPassword(e.target.value)}
          />
          <button
            className="py-3 px-6 bg-gradient-to-br from-[#1F5683] to-[#246399] text-white border-none rounded-full cursor-pointer text-base hover:opacity-90"
            onClick={login}
          >
            Login
          </button>
        </div>
      </div>
    );
  }

  // **Dashboard view**
  return (
    <div className="p-5 bg-black min-h-screen text-white">
      <header className="text-center mb-5">
        <img src="/LogoFile.png" alt="Myte Logo" className="max-w-[100px] mb-2 mx-auto" />
        <h1 className="text-4xl text-white">Cooper Requests Dashboard</h1>
        <p className="text-lg mt-1 text-white">
          Review, Approve, Reject, and Fulfill Equipment Requests
        </p>
      </header>

      <div className="text-center mb-5 flex flex-wrap justify-center gap-2">
        {['ALL', 'OPENED', 'APPROVED', 'REJECTED', 'FULFILLED'].map((f) => (
          <button
            key={f}
            className={`py-2 px-4 rounded-full text-sm ${
              filter === f
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-800 hover:bg-blue-600 hover:text-white'
            }`}
            onClick={() => setFilter(f)}
          >
            {f.charAt(0) + f.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      <button
        className="block mx-auto mb-5 py-2 px-4 bg-blue-600 text-white border-none rounded-full cursor-pointer text-sm hover:bg-blue-700"
        onClick={fetchRequests}
      >
        Refresh
      </button>

      {isMobile ? renderCards() : renderTable()}
    </div>
  );
};

export default CooperDashboard;